import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtINR, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Users, Plus, Download, GitMerge } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Parties() {
  const [parties, setParties] = useState(null);
  const [tab, setTab] = useState("all");
  const [form, setForm] = useState({ name: "", role: "buyer", notes: "" });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/parties").then((r) => setParties(r.data));
  useEffect(() => { load(); }, []);

  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("");
  const [mergeRole, setMergeRole] = useState("seller");
  const [mergeTarget, setMergeTarget] = useState("");
  const [mergeSources, setMergeSources] = useState([]);
  const [merging, setMerging] = useState(false);
  useEffect(() => {
    api.get("/seller-categories").then((r) => {
      const list = r.data.category_list || [];
      setCategories(list);
      if (list.length) setCat(list[0]);
    }).catch(() => {});
  }, []);

  const downloadCategoryPdf = async () => {
    if (!cat) { toast.error("Pick a category seller."); return; }
    try {
      const res = await api.get("/reports/category/pdf", { params: { category: cat }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `category-${cat}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Report failed."); }
  };

  const doMerge = async () => {
    const sources = mergeSources.filter((s) => s && s.toLowerCase() !== mergeTarget.toLowerCase());
    if (!mergeTarget) { toast.error("Pick the canonical (target) name."); return; }
    if (sources.length === 0) { toast.error("Select at least one duplicate to merge in."); return; }
    if (!window.confirm(`Merge ${sources.length} name(s) into "${mergeTarget}"? All their orders will be re-pointed.`)) return;
    setMerging(true);
    try {
      const { data } = await api.post("/parties/merge", { role: mergeRole, sources, target: mergeTarget });
      toast.success(`Merged into ${mergeTarget} — ${data.updated_orders} orders re-pointed.`);
      setMergeSources([]); setMergeTarget("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Merge failed.");
    } finally { setMerging(false); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Enter a name."); return; }
    setSaving(true);
    try {
      await api.post("/parties", form);
      toast.success("Party added.");
      setForm({ name: "", role: "buyer", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed.");
    } finally { setSaving(false); }
  };

  if (!parties) return <div className="text-slate-400 font-mono text-sm">Loading parties…</div>;

  const filtered = parties.filter((p) => tab === "all" || p.role === tab);

  const normP = (s) => (s || "").toUpperCase().replace(/[^A-Z0-9]+/g, " ").trim();
  const suggestions = (() => {
    const map = {};
    parties.filter((p) => p.role === mergeRole).forEach((p) => {
      const toks = normP(p.name).split(" ").filter(Boolean);
      if (!toks.length) return;
      const key = toks.slice(0, Math.min(2, toks.length)).join(" ");
      (map[key] = map[key] || []).push(p);
    });
    return Object.values(map)
      .filter((g) => g.length > 1)
      .map((g) => g.slice().sort((a, b) => b.order_count - a.order_count))
      .sort((a, b) => b.length - a.length);
  })();
  const applySuggestion = (g) => {
    setMergeTarget(g[0].name);
    setMergeSources(g.slice(1).map((x) => x.name));
  };

  return (
    <div className="space-y-6" data-testid="parties-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#8CA3A7]">Directory</p>
        <h1 className="font-heading text-4xl font-bold text-[#0A0D14] tracking-tight mt-1">Parties</h1>
        <p className="text-sm text-[#8CA3A7] mt-2">Your buyers (garment exporters) and sellers (weavers/mills) with running totals.</p>
      </div>

      <div className="bg-white border border-[#E6E4DD] rounded-sm p-5 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="category-report-card">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Category Seller Report (sub-seller-wise → buyer-wise)</label>
          <select data-testid="category-select" value={cat} onChange={(e) => setCat(e.target.value)} className="mt-1 w-full sm:max-w-xs rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
            {categories.length === 0 ? <option value="">No categories</option> : categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button data-testid="download-category-report-btn" onClick={downloadCategoryPdf} className="bg-[#1C2331] text-white rounded-sm px-4 py-2 text-sm font-medium hover:bg-[#2A3447] transition-colors flex items-center gap-1.5">
          <Download className="w-3.5 h-3.5" /> Download PDF
        </button>
      </div>

      {isAdmin && (
      <div className="bg-white border border-[#E6E4DD] rounded-sm p-5" data-testid="merge-parties-card">
        <h2 className="font-heading text-lg font-semibold text-[#0A0D14] flex items-center gap-2 mb-1"><GitMerge className="w-4 h-4 text-[#D95D39]" /> Merge duplicate parties</h2>
        <p className="text-xs text-[#8CA3A7] mb-4">Combine different spellings of the same party into one. All their orders get re-pointed to the canonical name.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Role</label>
            <select data-testid="merge-role" value={mergeRole} onChange={(e) => { setMergeRole(e.target.value); setMergeTarget(""); setMergeSources([]); }} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
              <option value="seller">Sellers</option>
              <option value="buyer">Buyers</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Keep this name (canonical)</label>
            <select data-testid="merge-target" value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
              <option value="">— select —</option>
              {parties.filter((p) => p.role === mergeRole).map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button data-testid="do-merge-btn" onClick={doMerge} disabled={merging} className="w-full bg-[#1C2331] text-white rounded-sm px-4 py-2 text-sm font-medium hover:bg-[#2A3447] transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5">
              <GitMerge className="w-3.5 h-3.5" /> {merging ? "Merging…" : "Merge selected"}
            </button>
          </div>
        </div>
        {suggestions.length > 0 && (
          <div className="mt-4 border-t border-[#E6E4DD] pt-3" data-testid="merge-suggestions">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Likely duplicates ({suggestions.length})</label>
            <div className="mt-1 flex flex-col gap-1.5 max-h-40 overflow-y-auto">
              {suggestions.map((g, i) => (
                <button key={i} type="button" data-testid={`merge-suggestion-${i}`} onClick={() => applySuggestion(g)} className="text-left text-xs px-3 py-2 rounded-sm border border-[#FDE68A] bg-[#FFFBEB] hover:bg-[#FEF3C7] transition-colors">
                  <span className="text-[#92400E]">Merge into </span>
                  <span className="font-semibold text-[#0A0D14]">{g[0].name}</span>
                  <span className="text-[#8CA3A7]"> ← {g.slice(1).map((x) => x.name).join(", ")}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="mt-3">
          <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Duplicates to merge in {mergeTarget ? `(→ ${mergeTarget})` : ""}</label>
          <div className="mt-1 flex flex-wrap gap-2 max-h-36 overflow-y-auto">
            {parties.filter((p) => p.role === mergeRole && p.name !== mergeTarget).map((p) => {
              const on = mergeSources.includes(p.name);
              return (
                <button key={p.name} type="button" data-testid={`merge-src-${p.name}`} onClick={() => setMergeSources((prev) => on ? prev.filter((x) => x !== p.name) : [...prev, p.name])} className={`text-xs px-2 py-1 rounded-sm border transition-colors ${on ? "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]" : "bg-white text-[#4B5563] border-[#E6E4DD] hover:border-[#1C2331]"}`}>
                  {on ? "✓ " : ""}{p.name} <span className="text-[#8CA3A7]">({p.order_count})</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Add form */}
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-6 h-fit">
          <h2 className="font-heading text-lg font-semibold text-[#0A0D14] flex items-center gap-2 mb-4"><Plus className="w-4 h-4 text-[#D95D39]" /> Add party</h2>
          <form onSubmit={add} className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Name</label>
              <input data-testid="party-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Role</label>
              <select data-testid="party-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
                <option value="buyer">Buyer (Garment Exporter)</option>
                <option value="seller">Seller (Weaver / Mill)</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Notes</label>
              <input data-testid="party-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
            </div>
            <button data-testid="save-party-btn" disabled={saving} className="w-full bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#2A3447] transition-colors disabled:opacity-60">
              {saving ? "Saving…" : "Add Party"}
            </button>
          </form>
        </div>

        {/* List */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex gap-2">
            {[["all", "All"], ["buyer", "Buyers"], ["seller", "Sellers"]].map(([v, l]) => (
              <button
                key={v}
                data-testid={`party-tab-${v}`}
                onClick={() => setTab(v)}
                className={`text-sm px-3 py-1.5 rounded-sm border transition-colors ${tab === v ? "bg-[#1C2331] text-white border-[#1C2331]" : "bg-white text-[#4B5563] border-[#E6E4DD] hover:border-[#1C2331]"}`}
              >
                {l}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white border border-[#E6E4DD] rounded-sm p-12 text-center">
              <Users className="w-8 h-8 text-[#8CA3A7] mx-auto mb-3" />
              <p className="text-sm text-[#4B5563]">No parties yet. Add one, or create orders to populate this list.</p>
            </div>
          ) : (
            <div className="border border-[#E6E4DD] rounded-sm bg-white overflow-hidden">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="font-mono text-[10px] uppercase tracking-wider text-[#8CA3A7] bg-slate-50 border-b border-[#E6E4DD]">
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3 text-right">Orders</th>
                    <th className="px-4 py-3 text-right">Total Value</th>
                    <th className="px-4 py-3 text-right">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={`${p.role}-${p.name}`} data-testid={`party-row-${p.name}`} onClick={() => navigate(`/parties/detail?name=${encodeURIComponent(p.name)}&role=${p.role}`)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors cursor-pointer">
                      <td className="px-4 py-3 text-[#0A0D14]">{p.name}{p.notes ? <span className="block text-xs text-[#8CA3A7]">{p.notes}</span> : null}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border ${p.role === "buyer" ? "bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]" : "bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]"}`}>{p.role}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-[#4B5563]">{p.order_count}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-[#0A0D14]">{fmtINR(p.total_value)}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-[#D95D39]">{fmtINR(p.outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
